import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Mail,
  ChevronDown,
  ChevronUp,
  X,
  RefreshCw,
  Clock,
  AlertTriangle,
  ShieldCheck,
  Download,
} from 'lucide-react';
import {
  getClients,
  getMessageTrace,
  getMessageTraceDetail,
  MessageTraceResult,
  MessageTraceEvent,
} from '../../services/api';
import EmailAuthResults from '../EmailAuth/EmailAuthResults';
import { Client } from '../../types';
import { format, subDays } from 'date-fns';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Quarantined', label: 'Quarantined' },
  { value: 'FilteredAsSpam', label: 'Filtered as Spam' },
  { value: 'Expanded', label: 'Expanded' },
];

function getStatusBadge(status: string) {
  const s = status.toLowerCase();
  if (s === 'delivered') {
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
  }
  if (s === 'failed') {
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
  }
  if (s === 'pending' || s === 'gettingstatus') {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
  }
  if (s === 'quarantined' || s === 'filteredasspam') {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
  }
  if (s === 'expanded') {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
  }
  return 'bg-muted text-foreground';
}

function getEventColor(event: string) {
  const e = event.toLowerCase();
  if (e === 'deliver' || e === 'delivered') return 'text-green-600 dark:text-green-400';
  if (e === 'fail' || e === 'failed') return 'text-red-600 dark:text-red-400';
  if (e === 'defer' || e === 'deferred') return 'text-yellow-600 dark:text-yellow-400';
  if (e === 'spam' || e === 'quarantine') return 'text-orange-600 dark:text-orange-400';
  if (e === 'receive' || e === 'submit') return 'text-blue-600 dark:text-blue-400';
  return 'text-muted-foreground';
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDateForInput(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export default function MessageTracePage() {
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('clientId') || '';

  // Client selection
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState(preselectedClientId);

  // Search filters
  const [senderAddress, setSenderAddress] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [startDate, setStartDate] = useState(formatDateForInput(subDays(new Date(), 2)));
  const [endDate, setEndDate] = useState(formatDateForInput(new Date()));
  const [subjectFilter, setSubjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [messageId, setMessageId] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Results
  const [messages, setMessages] = useState<MessageTraceResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Email Auth
  const [showEmailAuth, setShowEmailAuth] = useState(false);

  // Detail panel
  const [selectedMessage, setSelectedMessage] = useState<MessageTraceResult | null>(null);
  const [detailEvents, setDetailEvents] = useState<MessageTraceEvent[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Client-side subject filter on results
  const filteredMessages = useMemo(() => {
    if (!subjectFilter.trim()) return messages;
    const needle = subjectFilter.toLowerCase();
    return messages.filter(
      (msg) => msg.subject && msg.subject.toLowerCase().includes(needle)
    );
  }, [messages, subjectFilter]);

  // CSV export
  const handleExportCsv = useCallback(() => {
    if (filteredMessages.length === 0) return;
    const headers = ['Date/Time', 'Sender', 'Recipient', 'Subject', 'Status', 'Size (bytes)', 'Message ID'];
    const rows = filteredMessages.map((msg) => [
      msg.received ? format(new Date(msg.received), 'yyyy-MM-dd HH:mm:ss') : '',
      msg.senderAddress || '',
      msg.recipientAddress || '',
      (msg.subject || '').replace(/"/g, '""'),
      msg.status || '',
      String(msg.size || 0),
      (msg.messageId || '').replace(/"/g, '""'),
    ]);
    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `message-trace-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredMessages]);

  // Load clients on mount
  useEffect(() => {
    async function loadClients() {
      try {
        const response = await getClients({ pageSize: 500 });
        setClients(response.items || []);
      } catch {
        // silently fail - clients dropdown will be empty
      } finally {
        setClientsLoading(false);
      }
    }
    loadClients();
  }, []);

  const handleSearch = useCallback(async () => {
    if (!selectedClientId) {
      setError('Please select a client');
      return;
    }

    if (!senderAddress && !recipientAddress) {
      setError('Please enter at least a sender or recipient email address');
      return;
    }

    // Validate date range (max 10 days)
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 10) {
      setError('Date range cannot exceed 10 days (Exchange Online limitation)');
      return;
    }
    if (daysDiff < 0) {
      setError('End date must be after start date');
      return;
    }

    setLoading(true);
    setError(null);
    setMessages([]);
    setSelectedMessage(null);
    setHasSearched(true);

    try {
      const result = await getMessageTrace(selectedClientId, {
        senderAddress: senderAddress || undefined,
        recipientAddress: recipientAddress || undefined,
        startDate,
        endDate,
        messageId: messageId || undefined,
        status: statusFilter || undefined,
      });

      if (result.success) {
        setMessages(result.messages || []);
        setTotalCount(result.totalCount || 0);
      } else {
        setError(result.error || 'Failed to run message trace');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, senderAddress, recipientAddress, startDate, endDate, messageId, statusFilter]);

  const handleViewDetail = useCallback(async (message: MessageTraceResult) => {
    if (selectedMessage?.messageTraceId === message.messageTraceId) {
      setSelectedMessage(null);
      return;
    }

    setSelectedMessage(message);
    setDetailLoading(true);
    setDetailError(null);
    setDetailEvents([]);

    try {
      const result = await getMessageTraceDetail(
        selectedClientId,
        message.messageTraceId,
        message.recipientAddress,
        message.messageId || undefined
      );

      if (result.success) {
        setDetailEvents(result.events || []);
      } else {
        setDetailError(result.error || 'Failed to get message detail');
      }
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDetailLoading(false);
    }
  }, [selectedClientId, selectedMessage]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Mail className="h-7 w-7 text-primary-600" />
            Message Trace
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Trace email delivery through Exchange Online. Results limited to the last 10 days.
          </p>
        </div>
      </div>

      {/* Search Form */}
      <div className="bg-card shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Client Selector */}
          <div className="lg:col-span-3">
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Client
            </label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              disabled={clientsLoading}
              className="input w-full md:w-96"
            >
              <option value="">{clientsLoading ? 'Loading clients...' : 'Select a client...'}</option>
              {clients
                .sort((a, b) => a.companyName.localeCompare(b.companyName))
                .map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.companyName}
                  </option>
                ))}
            </select>
          </div>

          {/* Sender */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Sender Email
            </label>
            <input
              type="email"
              value={senderAddress}
              onChange={(e) => setSenderAddress(e.target.value)}
              placeholder="sender@example.com"
              className="input w-full"
            />
          </div>

          {/* Recipient */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Recipient Email
            </label>
            <input
              type="email"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              placeholder="recipient@example.com"
              className="input w-full"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Subject Filter */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Subject Contains
            </label>
            <input
              type="text"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              placeholder="Filter by subject..."
              className="input w-full"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate}
              className="input w-full"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={formatDateForInput(new Date())}
              className="input w-full"
            />
          </div>

          {/* Advanced Toggle */}
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1"
            >
              {showAdvanced ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              Advanced
            </button>
          </div>

          {/* Advanced: Message ID */}
          {showAdvanced && (
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-muted-foreground mb-1">
                Message ID
              </label>
              <input
                type="text"
                value={messageId}
                onChange={(e) => setMessageId(e.target.value)}
                placeholder="<message-id@example.com>"
                className="input w-full"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                The Message-ID header value from the email headers.
              </p>
            </div>
          )}
        </div>

        {/* Search Button */}
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={handleSearch}
            disabled={loading || !selectedClientId}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Running message trace...
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Search
              </>
            )}
          </button>
          {selectedClientId && (
            <button
              onClick={() => setShowEmailAuth(!showEmailAuth)}
              className={`btn-secondary flex items-center gap-2 ${showEmailAuth ? 'text-primary-600 border-primary-300 dark:text-primary-400 dark:border-primary-700' : ''}`}
            >
              <ShieldCheck className="h-4 w-4" />
              Check Email Auth
            </button>
          )}
          {filteredMessages.length > 0 && (
            <button
              onClick={handleExportCsv}
              className="btn-secondary flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          )}
          {loading && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Clock className="h-4 w-4" />
              This may take 30-60 seconds...
            </span>
          )}
        </div>
      </div>

      {/* Email Auth Results */}
      {showEmailAuth && selectedClientId && (
        <EmailAuthResults
          clientId={selectedClientId}
          onClose={() => setShowEmailAuth(false)}
        />
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {hasSearched && !loading && (
        <div className="bg-card shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-medium text-foreground">
              Results
              {totalCount > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({subjectFilter.trim()
                    ? `${filteredMessages.length} of ${totalCount} messages`
                    : `${totalCount} ${totalCount === 1 ? 'message' : 'messages'}`})
                </span>
              )}
            </h2>
          </div>

          {filteredMessages.length === 0 ? (
            <div className="p-12 text-center">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                No messages found matching your search criteria.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Try broadening your date range or adjusting filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Date/Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Sender
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Recipient
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Subject
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Size
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredMessages.map((msg) => (
                    <tr
                      key={msg.messageTraceId}
                      onClick={() => handleViewDetail(msg)}
                      className={`cursor-pointer transition-colors ${
                        selectedMessage?.messageTraceId === msg.messageTraceId
                          ? 'bg-primary-50 dark:bg-primary-900/30'
                          : 'hover:bg-accent'
                      }`}
                    >
                      <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">
                        {msg.received
                          ? format(new Date(msg.received), 'MMM d, yyyy h:mm a')
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                        {msg.senderAddress}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                        {msg.recipientAddress}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground max-w-[250px] truncate">
                        {msg.subject || '(no subject)'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                            msg.status
                          )}`}
                        >
                          {msg.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                        {formatSize(msg.size || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Detail Panel */}
      {selectedMessage && (
        <div className="bg-card shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-medium text-foreground">
              Message Detail
            </h3>
            <button
              onClick={() => setSelectedMessage(null)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Summary */}
          <div className="px-6 py-4 border-b border-border bg-muted">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="font-medium text-muted-foreground">Subject</dt>
                <dd className="text-foreground">{selectedMessage.subject || '(no subject)'}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Sender</dt>
                <dd className="text-foreground">{selectedMessage.senderAddress}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Recipient</dt>
                <dd className="text-foreground">{selectedMessage.recipientAddress}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Date</dt>
                <dd className="text-foreground">
                  {selectedMessage.received
                    ? format(new Date(selectedMessage.received), 'MMM d, yyyy h:mm:ss a')
                    : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Status</dt>
                <dd>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                      selectedMessage.status
                    )}`}
                  >
                    {selectedMessage.status}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Size</dt>
                <dd className="text-foreground">{formatSize(selectedMessage.size || 0)}</dd>
              </div>
              {selectedMessage.messageId && (
                <div className="lg:col-span-3">
                  <dt className="font-medium text-muted-foreground">Message ID</dt>
                  <dd className="text-foreground font-mono text-xs break-all">
                    {selectedMessage.messageId}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Routing Events */}
          <div className="px-6 py-4">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">
              Routing Events
            </h4>

            {detailLoading && (
              <div className="flex items-center gap-2 py-8 justify-center">
                <RefreshCw className="h-5 w-5 animate-spin text-primary-600" />
                <span className="text-sm text-muted-foreground">
                  Loading routing events (may take 30-60s)...
                </span>
              </div>
            )}

            {detailError && (
              <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{detailError}</p>
              </div>
            )}

            {!detailLoading && !detailError && detailEvents.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No routing events found.
              </p>
            )}

            {!detailLoading && detailEvents.length > 0 && (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />

                <div className="space-y-4">
                  {detailEvents
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((event, i) => (
                      <div key={i} className="relative pl-10">
                        {/* Timeline dot */}
                        <div
                          className={`absolute left-2.5 top-1 w-3 h-3 rounded-full border-2 border-card ${
                            event.event.toLowerCase().includes('deliver')
                              ? 'bg-green-500'
                              : event.event.toLowerCase().includes('fail')
                              ? 'bg-red-500'
                              : event.event.toLowerCase().includes('defer')
                              ? 'bg-yellow-500'
                              : 'bg-blue-500'
                          }`}
                        />

                        <div className="bg-muted rounded-md p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm font-medium ${getEventColor(event.event)}`}>
                              {event.event}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {event.date
                                ? format(new Date(event.date), 'MMM d, h:mm:ss a')
                                : ''}
                            </span>
                          </div>
                          {event.action && (
                            <p className="text-sm text-muted-foreground">{event.action}</p>
                          )}
                          {event.detail && (
                            <p className="text-xs text-muted-foreground mt-1 break-all">
                              {event.detail}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
